import {useEffect, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import axios from 'axios';
import moment, {Moment} from 'moment';
import {v4 as uuidv4} from 'uuid';
import {Alert, Divider, Grid, Stack, Typography} from '@mui/material';
import {
  AttendanceTrackerRegistryRecordError,
  AttendanceTrackerRegistrySession,
} from '../../backend/responses/attendance-tracker-registry';
import {useAuth} from '../../contexts/auth/context';
import {useSnackBar} from '../../contexts/snack-bar/context';
import {translations} from '../../i18n/translations';
import {EntityAttendanceTrackerRegistryRepository} from '../../repositories/entity-attendance-tracker-registry';
import FlatInput from '../flat-input/flat-input';
import {FlatSelectDropdown, Option} from '../flat-select-dropdown/flat-select-dropdown';
import {Modal} from '../modal/modal';
import {DateTimeFields, REASON_MAX_LENGTH, SummaryRow} from './attendance-registry-form-parts';

type Kind = 'session' | 'entry';

type AttendanceRegistryCorrectDialogProps = {
  open: boolean;
  onClose: () => void;
  entityId: string;
  /** The session to correct */
  session?: AttendanceTrackerRegistrySession;
  /** Called after the session was corrected, so the list can be loaded again */
  onCorrected: () => void;
};

const formatDateTime = (value: Moment) => value.format('DD/MM/YYYY HH:mm');

/**
 * Corrects a session of the registry: it is voided and the records with the right data are registered in its
 * place, in the same operation. The time that the session had is free for the new records.
 */
export default function AttendanceRegistryCorrectDialog({
  open,
  onClose,
  entityId,
  session,
  onCorrected,
}: AttendanceRegistryCorrectDialogProps) {
  const {t} = useTranslation();
  const {state} = useAuth();
  const snackBarContext = useSnackBar();
  const texts = translations.attendance_tracker_registry_screen.correct;
  const addTexts = translations.attendance_tracker_registry_screen.add;
  const voidTexts = translations.attendance_tracker_registry_screen.void;
  const dailyCareLogTexts = translations.daily_care_log.attendance_tracker;

  const currentUser = state.session?.currentUser;

  const [step, setStep] = useState<'form' | 'confirm'>('form');
  const [kind, setKind] = useState<Kind>('session');
  const [entryAt, setEntryAt] = useState<Moment>(() => moment());
  const [exitAt, setExitAt] = useState<Moment>(() => moment());
  const [reason, setReason] = useState('');
  const [hasTriedToContinue, setHasTriedToContinue] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [serverError, setServerError] = useState<string | undefined>(undefined);
  // Generated once for each correction, so a retry does not make it twice
  const clientRequestIdRef = useRef(uuidv4());
  const isSavingRef = useRef(false);

  const needsExit = kind === 'session';

  // Starts from the data of the session, every time it is opened
  useEffect(() => {
    if (!open || !session) {
      return;
    }

    setStep('form');
    setKind(session.exit ? 'session' : 'entry');
    setEntryAt(moment(session.entry.registeredAt).set({second: 0, millisecond: 0}));
    setExitAt(
      session.exit
        ? moment(session.exit.registeredAt).set({second: 0, millisecond: 0})
        : moment().set({second: 0, millisecond: 0})
    );
    setReason('');
    setHasTriedToContinue(false);
    setServerError(undefined);
    clientRequestIdRef.current = uuidv4();
    // Only when it is opened (or for another session), not every time the session is loaded again
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, session?.id]);

  const kindOptions: Option[] = [
    {key: 'session', label: t(addTexts.kind_session)},
    {key: 'entry', label: t(addTexts.kind_entry)},
  ];

  // Mistakes that can be told without asking the backend
  const errors = useMemo(() => {
    const now = moment();

    return {
      entry: entryAt.isAfter(now) ? t(addTexts.validation_future) : undefined,
      exit: !needsExit
        ? undefined
        : exitAt.isAfter(now)
          ? t(addTexts.validation_future)
          : !exitAt.isAfter(entryAt)
            ? t(addTexts.validation_exit_before_entry)
            : undefined,
      reason: reason.trim().length === 0 ? t(addTexts.validation_reason_required) : undefined,
    };
  }, [entryAt, exitAt, needsExit, reason, t, addTexts]);

  const canContinue = !errors.entry && !errors.exit && !errors.reason;

  const handleContinue = () => {
    setHasTriedToContinue(true);
    if (canContinue) {
      setServerError(undefined);
      setStep('confirm');
    }
  };

  const errorMessage = (error: unknown): string => {
    const code = axios.isAxiosError<AttendanceTrackerRegistryRecordError>(error)
      ? error.response?.data?.code
      : undefined;

    switch (code) {
      case 'future':
        return t(addTexts.error_future);
      case 'order':
        return t(addTexts.error_order);
      case 'overlap':
        return t(addTexts.error_overlap);
      case 'type':
        return t(addTexts.error_type);
      case 'conflict':
        return t(addTexts.error_conflict);
      case 'professional-not-tracking-attendance':
        return t(addTexts.error_not_tracking);
      case 'self-void-not-allowed':
        return t(voidTexts.error_self);
      default:
        return t(addTexts.error_generic);
    }
  };

  const handleCorrect = async () => {
    if (!session || isSavingRef.current) {
      return;
    }

    isSavingRef.current = true;
    setIsSaving(true);
    setServerError(undefined);
    try {
      await EntityAttendanceTrackerRegistryRepository.instance.replaceSession(
        entityId,
        session.id,
        {
          entryAt: entryAt.toISOString(),
          exitAt: needsExit ? exitAt.toISOString() : undefined,
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          reason: reason.trim(),
          clientRequestId: clientRequestIdRef.current,
        }
      );

      snackBarContext.setOfficeToastNotification({severity: 'success', message: t(texts.success)});
      onCorrected();
      onClose();
    } catch (e) {
      console.error(e);
      setServerError(errorMessage(e));
      // The records of the professional may have changed: let the user confirm the data again
      setStep('form');
    } finally {
      isSavingRef.current = false;
      setIsSaving(false);
    }
  };

  const currentSession = session
    ? `${formatDateTime(moment(session.entry.registeredAt))} → ${
        session.exit
          ? formatDateTime(moment(session.exit.registeredAt))
          : t(dailyCareLogTexts.details.no_exit)
      }`
    : undefined;
  const newSession = `${formatDateTime(entryAt)} → ${
    needsExit ? formatDateTime(exitAt) : t(dailyCareLogTexts.details.no_exit)
  }`;
  const notice = t(texts.notice, {admin: currentUser?.name, professional: session?.user.name});

  return (
    <Modal
      id="attendance-registry-correct"
      title={t(step === 'form' ? texts.title : texts.confirm_title)}
      open={open}
      onClose={onClose}
      preventCloseClick={isSaving}
      actionButton={{type: 'close'}}
      fullWidth
      maxWidth="sm"
      actions={
        step === 'form'
          ? [
              {label: t(addTexts.cancel), type: 'destructive', onClick: onClose},
              {
                label: t(addTexts.continue),
                type: 'nondestructive',
                disabled: hasTriedToContinue ? !canContinue : false,
                onClick: handleContinue,
              },
            ]
          : [
              {
                label: t(addTexts.back),
                type: 'destructive',
                disabled: isSaving,
                onClick: () => setStep('form'),
              },
              {
                label: t(texts.confirm),
                type: 'nondestructive',
                loading: isSaving,
                onClick: handleCorrect,
              },
            ]
      }
    >
      {session && (
        <Stack gap="20px">
          {serverError && <Alert severity="error">{serverError}</Alert>}

          {step === 'form' ? (
            <>
              <Alert severity="info">{t(texts.description)}</Alert>

              <SummaryRow label={t(addTexts.professional)} value={session.user.name} />
              <SummaryRow label={t(texts.current_session)} value={currentSession} />

              <FlatSelectDropdown
                label={t(addTexts.kind_label)}
                options={kindOptions}
                value={kindOptions.find(({key}) => key === kind)}
                onChange={(option) => setKind(option.key as Kind)}
              />

              <Grid container columnSpacing={2} rowSpacing={3}>
                <DateTimeFields
                  dateLabel={t(addTexts.entry_date)}
                  timeLabel={t(addTexts.entry_time)}
                  value={entryAt}
                  errors={hasTriedToContinue ? errors.entry : undefined}
                  onChange={setEntryAt}
                />
                {needsExit && (
                  <DateTimeFields
                    dateLabel={t(addTexts.exit_date)}
                    timeLabel={t(addTexts.exit_time)}
                    value={exitAt}
                    errors={hasTriedToContinue ? errors.exit : undefined}
                    onChange={setExitAt}
                  />
                )}
              </Grid>

              <FlatInput
                label={t(texts.reason)}
                placeholder={t(addTexts.reason_placeholder)}
                required
                multiline
                minRows={2}
                value={reason}
                onChange={(event) => setReason(event.target.value.slice(0, REASON_MAX_LENGTH))}
                counter={{current: reason.length, max: REASON_MAX_LENGTH}}
                errors={hasTriedToContinue ? errors.reason : undefined}
              />

              <Alert severity="info">{notice}</Alert>
            </>
          ) : (
            <>
              <Typography sx={{fontSize: '14px'}}>{t(texts.confirm_description)}</Typography>
              <Divider />
              <SummaryRow label={t(addTexts.professional)} value={session.user.name} />
              <SummaryRow label={t(texts.summary_old)} value={currentSession} />
              <SummaryRow label={t(texts.summary_new)} value={newSession} />
              <SummaryRow label={t(texts.reason)} value={reason.trim()} />
              <Alert severity="info">{notice}</Alert>
            </>
          )}
        </Stack>
      )}
    </Modal>
  );
}
